import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "CEO / Founder",
  "Brand Director",
  "Merchandising Head",
  "Design Lead",
  "CTO / Technology",
  "Marketing Director",
  "Other",
];

const FIELD_CLASS =
  "w-full bg-transparent border-0 border-b-2 border-[#0A0A0A]/30 focus:border-[#0A0A0A] focus:outline-none rounded-none px-0 py-3 text-[#0A0A0A] placeholder:text-[#0A0A0A]/35 font-manrope text-base transition-colors duration-200";

const DemoForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/demo`, form);
      setStatus("success");
      setForm({ name: "", email: "", company: "", role: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      data-testid="demo-form-section"
      className="bg-[#E8380D] py-24 md:py-40"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="max-w-2xl mb-12 md:mb-16"
        >
          <p className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-black/50 mb-4">
            Get Started
          </p>
          <h2 className="font-cabinet text-4xl sm:text-5xl font-bold uppercase tracking-tight text-[#0A0A0A] leading-tight mb-5">
            LET'S TRANSFORM YOUR
            <br />
            RETAIL OPERATIONS.
          </h2>
          <p className="font-manrope text-base md:text-lg text-black/70 leading-relaxed">
            Book a 30-minute session with our team. No pitch decks. No
            slideshows. Just answers.
          </p>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          viewport={{ once: true }}
          className="bg-[#F4F4F0] p-8 md:p-12 lg:p-16 max-w-3xl"
        >
          {status === "success" ? (
            <div
              data-testid="form-success-message"
              className="py-12 text-center"
            >
              <div className="font-cabinet text-5xl font-black text-[#0A0A0A] uppercase mb-4">
                You're in.
              </div>
              <p className="font-manrope text-base text-[#4A4A4A] mb-8">
                We've received your request and will reach out within 24 hours.
              </p>
              <button
                data-testid="form-reset-btn"
                onClick={() => setStatus("idle")}
                className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60 border-b border-[#0A0A0A]/40 hover:text-[#0A0A0A] transition-colors"
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form
              data-testid="demo-form"
              onSubmit={handleSubmit}
              className="space-y-8"
            >
              {/* Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/50 block mb-2">
                    Full Name *
                  </label>
                  <input
                    data-testid="form-name-input"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Jane Smith"
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/50 block mb-2">
                    Work Email *
                  </label>
                  <input
                    data-testid="form-email-input"
                    name="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                    placeholder="jane@yourband.com"
                    className={FIELD_CLASS}
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/50 block mb-2">
                    Company *
                  </label>
                  <input
                    data-testid="form-company-input"
                    name="company"
                    type="text"
                    required
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Brand Co."
                    className={FIELD_CLASS}
                  />
                </div>
                <div>
                  <label className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/50 block mb-2">
                    Your Role *
                  </label>
                  <select
                    data-testid="form-role-select"
                    name="role"
                    required
                    value={form.role}
                    onChange={handleChange}
                    className={`${FIELD_CLASS} cursor-pointer`}
                  >
                    <option value="" disabled>
                      Select your role
                    </option>
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/50 block mb-2">
                  What are you looking to solve? (Optional)
                </label>
                <textarea
                  data-testid="form-message-input"
                  name="message"
                  rows={3}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your current challenges..."
                  className={`${FIELD_CLASS} resize-none`}
                />
              </div>

              {/* Error */}
              {status === "error" && (
                <p
                  data-testid="form-error-message"
                  className="font-mono text-xs text-red-600 uppercase tracking-wider"
                >
                  Something went wrong. Please try again.
                </p>
              )}

              {/* Submit */}
              <button
                data-testid="form-submit-btn"
                type="submit"
                disabled={status === "loading"}
                className="w-full bg-[#00FF66] text-[#0A0A0A] font-cabinet font-black uppercase text-base tracking-widest py-5 hover:bg-[#0A0A0A] hover:text-[#00FF66] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === "loading" ? "SUBMITTING..." : "BOOK MY DEMO"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default DemoForm;
